import React from 'react';
import styled from '@emotion/styled';
import get from 'lodash/get';
import noop from 'lodash/noop';

import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  align?: 'center' | 'left' | 'right' | 'spaceBetween',
  ariaLabel?: string,
  bold?: boolean,
  children: React.Node,
  className?: ?string,
  color?: String,
  dataTest?: String,
  display: 'inline' | 'block' | 'flex' | 'inline-block',
  isHTML?: boolean,
  level?: 1 | 2 | 3 | 4 | 5,
  margin?: 'none' | 'small' | 'normal' | 'large',
  minWidth: number,
  mode?: string,
  onClick: Function,
  strong?: boolean,
  tag?: string,
  type?: 'success' | 'danger' | 'warning' | 'info' | 'brand' | 'black' | 'grey' | 'lightGrey' | 'lighterGrey' | 'white',
  underline?: boolean,
  vAlign?: 'center',
  whiteSpace: string,
};

/**
 * ### Available modes
 * * `Bolder`: Make the font weight `700`.
 * * `Introduction`: Text on the 'Introduction' page
 * * `KioskHeader`: Text within the User/Loyalty information in the Kiosk header
 * * `LoyaltyAuthCardLoggedIn`: Text within the LoyaltyAuthCard component on the checkout page when logged in
 * * `LoyaltyAuthCardLoggedOut`: Text within the LoyaltyAuthCard component on the checkout page when logged out
 * * `NutritionalLawText`: Text from the Menu Overview page (The law text)
 * * `StoreSelection:UnavailableDelivery`: Text from the Store Selection page for unavailable delivery
 * * `RewardCounter`: Text within the RewardCounter component
 * * `Welcome:MemberCode`: Text in the 'Member Code' section on the Welcome page
 * * `Welcome:OrderNow`: Text in the 'Ready to eat?' section on the Welcome page
 * * `Welcome:Rewards`: Text in the 'My Rewards' section on the Welcome page
 * */

export const Text = withSpacingStyles(
  ({
    align,
    ariaLabel,
    bold,
    children,
    className,
    color,
    dataTest,
    display = 'inline',
    isHTML,
    level = 3,
    margin,
    minWidth,
    mode,
    onClick,
    strong,
    tag,
    type,
    underline,
    vAlign,
    whiteSpace,
  }: Props) => {
    const componentTheme = useComponentTheme(theme => theme.components.Text, mode);

    return (
      <TextWrapper
        align={componentTheme.align || align}
        aria-label={ariaLabel}
        as={tag}
        bold={bold}
        className={className}
        color={color}
        componentTheme={componentTheme}
        data-test={dataTest}
        display={display}
        level={level}
        marginType={margin}
        minWidth={minWidth}
        strong={strong}
        type={type}
        underline={underline}
        vAlign={vAlign}
        whiteSpace={componentTheme.whiteSpace || whiteSpace}
        {...(isHTML ? { dangerouslySetInnerHTML: { __html: children.replace(/<\/?p[^>]*>/g, '') } } : { children })}
        {...(onClick && { onClick })}
      />
    );
  }
);

Text.displayName = 'Text';

const alignStyles = {
  center: `display: block; text-align: center;`,
  right: `display: block; text-align: right;`,
  left: `display: block; text-align: left;`,
  spaceBetween: `display: flex; justify-content: space-between;`,
};

const marginStyles = {
  xs: 'margin: 0 0 8px',
  small: 'margin: 0 0 16px',
  normal: 'margin: 0 0 24px',
  large: 'margin: 0 0 32px',
  none: 'margin: 0',
};

const textColor = (theme, componentTheme, type, color) => {
  if (type) return theme.color[type];
  if (color) return color;
  return get(componentTheme, 'color', 'inherit');
};

const vAlignStyles = {
  center: `display: flex; align-items: center;`,
};

const TextWrapper = styled.span`
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}px;` : '')}
  display: ${({ display }) => display};
  font-size: ${({ componentTheme, level }) => componentTheme.fontSize[`level${level}`]};
  ${({ marginType }) =>
    marginType &&
    `
    display: block;
    ${marginStyles[marginType]};
  `};
  ${({ align }) => align && alignStyles[align]};
  ${({ vAlign }) => vAlign && vAlignStyles[vAlign]};
  ${({ theme, componentTheme }) =>
    componentTheme.secondaryFontFamily && `font-family: ${theme.typography.secondaryFontFamily};`}
  font-weight: ${({ bold, strong, theme, componentTheme }) =>
    bold ? theme.fontWeight.bold : strong ? theme.fontWeight.medium : get(componentTheme, 'fontWeight', 'normal')};
  ${({ underline }) => underline && 'text-decoration: underline'};
  color: ${({ theme, componentTheme, type, color }) => textColor(theme, componentTheme, type, color)};
  cursor: ${({ onClick }) => onClick ? 'pointer' : 'default'};
  ${({ componentTheme }) => componentTheme.backgroundColor && `background-color: ${componentTheme.backgroundColor}`};
  ${({ componentTheme }) => componentTheme.textTransform && `text-transform: ${componentTheme.textTransform}`};
  ${({ whiteSpace }) => whiteSpace && `white-space: ${whiteSpace}`};
`;
